/**
 * Componente DashboardErrorBoundary
 * 
 * Error Boundary específico para o Dashboard, com UI de fallback
 * personalizada e logging de erros integrado.
 * 
 * @module components/Dashboard/DashboardErrorBoundary
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaExclamationTriangle, FaRedo, FaHome, FaChartLine } from 'react-icons/fa';
import { logComponentError } from '../../utils/errorLogger';

/**
 * Error Boundary específico para o Dashboard
 * 
 * Captura erros de renderização no dashboard e exibe uma UI de fallback
 * amigável com opções de recuperação.
 * 
 * @class DashboardErrorBoundary
 */
class DashboardErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: 0,
        };
    }

    /**
     * Método estático que é chamado quando um erro é lançado durante a renderização
     * 
     * @param {Error} error - O erro que foi lançado
     * @returns {object} Estado atualizado com informações do erro
     */
    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    /**
     * Método chamado após um erro ser lançado
     * 
     * @param {Error} error - O erro que foi lançado
     * @param {object} errorInfo - Informações sobre qual componente lançou o erro
     */
    componentDidCatch(error, errorInfo) {
        // Loga o erro em todos os serviços configurados
        logComponentError(error, errorInfo, {
            component: 'Dashboard',
            section: this.props.section || 'Unknown',
        });

        this.setState({
            error,
            errorInfo,
        });
    }

    /**
     * Handler para resetar o error boundary e tentar novamente
     */
    handleReset = () => {
        const { retryCount } = this.state;
        const maxRetries = this.props.maxRetries || 3;

        if (retryCount >= maxRetries) {
            // Após muitas tentativas, recarrega a página
            window.location.reload();
            return;
        }

        this.setState((prevState) => ({
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: prevState.retryCount + 1,
        }));
    };

    /**
     * Handler para recarregar a página completamente
     */
    handleReload = () => {
        window.location.reload();
    };

    /**
     * Handler para voltar para a home
     */
    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            const { error, errorInfo, retryCount } = this.state;
            const { 
                showDetails = false, 
                maxRetries = 3,
                section = 'Dashboard',
            } = this.props;

            const canRetry = retryCount < maxRetries;
            const isDevelopment = process.env.NODE_ENV === 'development';

            return (
                <div 
                    className="min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6"
                    role="alert"
                    aria-live="assertive"
                >
                    <div className="max-w-2xl w-full bg-white rounded-xl shadow-xl border border-red-200 p-6 sm:p-8">
                        {/* Ícone e Título */}
                        <div className="flex flex-col items-center mb-6">
                            <div className="bg-red-100 rounded-full p-4 mb-4">
                                <FaExclamationTriangle 
                                    className="text-red-600" 
                                    size={48}
                                    aria-hidden="true"
                                />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
                                Erro no {section}
                            </h2>
                            <p className="text-gray-600 text-center text-sm sm:text-base">
                                Ocorreu um erro inesperado ao carregar esta seção do dashboard.
                            </p>
                        </div>

                        {/* Mensagem de erro amigável */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <p className="text-sm text-yellow-800">
                                <strong>O que aconteceu?</strong>
                                <br />
                                Algo inesperado ocorreu ao renderizar o {section.toLowerCase()}. 
                                Isso pode ser um problema temporário ou um bug no sistema.
                            </p>
                        </div>

                        {/* Informações de retry */}
                        {canRetry && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                <p className="text-sm text-blue-800">
                                    <strong>Tentativa {retryCount + 1} de {maxRetries}</strong>
                                    <br />
                                    Você pode tentar novamente. Se o problema persistir, 
                                    recarregue a página ou entre em contato com o suporte.
                                </p>
                            </div>
                        )}

                        {/* Detalhes do erro (apenas em desenvolvimento ou se showDetails for true) */}
                        {(isDevelopment || showDetails) && error && (
                            <details className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <summary className="cursor-pointer text-sm font-semibold text-gray-700 mb-2">
                                    Detalhes técnicos (clique para expandir)
                                </summary>
                                <div className="mt-2 text-xs font-mono text-gray-600 overflow-auto max-h-64">
                                    <p className="font-semibold text-red-600 mb-2">
                                        {error.toString()}
                                    </p>
                                    {errorInfo && errorInfo.componentStack && (
                                        <pre className="whitespace-pre-wrap text-gray-500">
                                            {errorInfo.componentStack}
                                        </pre>
                                    )}
                                </div>
                            </details>
                        )}

                        {/* Botões de ação */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            {canRetry ? (
                                <button
                                    onClick={this.handleReset}
                                    className="
                                        flex items-center justify-center gap-2
                                        px-6 py-3
                                        bg-emerald-600 text-white
                                        rounded-lg
                                        font-semibold
                                        hover:bg-emerald-700
                                        focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                                        transition-colors duration-200
                                        disabled:opacity-50 disabled:cursor-not-allowed
                                    "
                                    aria-label="Tentar novamente"
                                >
                                    <FaRedo aria-hidden="true" />
                                    Tentar Novamente
                                </button>
                            ) : (
                                <button
                                    onClick={this.handleReload}
                                    className="
                                        flex items-center justify-center gap-2
                                        px-6 py-3
                                        bg-blue-600 text-white
                                        rounded-lg
                                        font-semibold
                                        hover:bg-blue-700
                                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                                        transition-colors duration-200
                                    "
                                    aria-label="Recarregar página"
                                >
                                    <FaRedo aria-hidden="true" />
                                    Recarregar Página
                                </button>
                            )}

                            <button
                                onClick={this.handleGoHome}
                                className="
                                    flex items-center justify-center gap-2
                                    px-6 py-3
                                    bg-gray-200 text-gray-700
                                    rounded-lg
                                    font-semibold
                                    hover:bg-gray-300
                                    focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                                    transition-colors duration-200
                                "
                                aria-label="Voltar para home"
                            >
                                <FaHome aria-hidden="true" />
                                Voltar para Home
                            </button>
                        </div>

                        {/* Link para suporte */}
                        <div className="mt-6 text-center">
                            <p className="text-xs text-gray-500">
                                Se o problema persistir, entre em contato com o{' '}
                                <a 
                                    href="/suporte" 
                                    className="text-emerald-600 hover:text-emerald-700 underline"
                                >
                                    suporte
                                </a>
                                {' '}ou{' '}
                                <a 
                                    href="mailto:suporte@higeia.tech" 
                                    className="text-emerald-600 hover:text-emerald-700 underline"
                                >
                                    envie um e-mail
                                </a>
                                .
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        // Se não há erro, renderiza os children normalmente
        return this.props.children;
    }
}

DashboardErrorBoundary.propTypes = {
    /** Componentes filhos a serem renderizados */
    children: PropTypes.node.isRequired,
    
    /** Nome da seção que está sendo protegida (para logging) */
    section: PropTypes.string,
    
    /** Se deve mostrar detalhes do erro mesmo em produção */
    showDetails: PropTypes.bool,
    
    /** Número máximo de tentativas antes de recarregar a página */
    maxRetries: PropTypes.number,
};

DashboardErrorBoundary.defaultProps = {
    section: 'Dashboard',
    showDetails: false,
    maxRetries: 3,
};

export default DashboardErrorBoundary;

